/**
 * Read R2 credentials out of whatever Cloudflare put on screen.
 *
 * Full automation is not possible from a browser: the Cloudflare API sends no
 * CORS headers, so this page cannot call it, and R2 must be switched on by hand
 * once per account regardless. What we can remove is the transcription — the
 * user selects the credentials panel Cloudflare shows them, pastes it once, and
 * the four values are pulled out here.
 *
 * Deliberately forgiving: it accepts the whole panel, a partial selection, an
 * .env fragment, or the values on their own lines in any order.
 */
import type { R2Config } from './r2';

export interface ParsedR2 extends Partial<R2Config> {
  /** Which fields were actually recognised, for showing the user. */
  found: (keyof R2Config)[];
}

const HEX32 = /\b[0-9a-f]{32}\b/gi;
const HEX64 = /\b[0-9a-f]{64}\b/gi;

/** `https://<account>.r2.cloudflarestorage.com`, optionally with /bucket. */
const ENDPOINT = /https?:\/\/([0-9a-f]{32})\.r2\.cloudflarestorage\.com(?:\/([^\s/"']+))?/i;

/** A labelled value, e.g. "Access Key ID: abc" or "R2_ACCESS_KEY_ID=abc". */
function labelled(text: string, ...labels: string[]): string | undefined {
  for (const label of labels) {
    const re = new RegExp(
      `${label.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&')}\\s*[:=]?\\s*["']?([A-Za-z0-9_\\-]{8,})["']?`,
      'i'
    );
    const m = re.exec(text);
    if (m?.[1]) return m[1];
  }
  return undefined;
}

export function parseR2Paste(raw: string): ParsedR2 {
  const text = raw.trim();
  const out: ParsedR2 = { found: [] };
  if (!text) return out;

  // The endpoint is the most reliable signal — it carries the account id, and
  // sometimes the bucket too.
  const ep = ENDPOINT.exec(text);
  if (ep) {
    out.accountId = ep[1]!.toLowerCase();
    if (ep[2]) out.bucket = ep[2];
  }

  out.accessKeyId ??= labelled(text, 'Access Key ID', 'AccessKeyId', 'R2_ACCESS_KEY_ID', 'aws_access_key_id');
  out.secretAccessKey ??= labelled(
    text,
    'Secret Access Key',
    'SecretAccessKey',
    'R2_SECRET_ACCESS_KEY',
    'aws_secret_access_key'
  );
  out.accountId ??= labelled(text, 'Account ID', 'AccountId', 'CLOUDFLARE_ACCOUNT_ID', 'R2_ACCOUNT_ID');
  out.bucket ??= labelled(text, 'Bucket name', 'Bucket', 'R2_BUCKET');

  // Fall back to shape: an R2 access key id is 32 hex, its secret is 64 hex.
  // Only used where a label did not already answer, so a labelled value always
  // wins over a guess.
  if (!out.secretAccessKey) {
    const sixtyFours = text.match(HEX64);
    if (sixtyFours?.length === 1) out.secretAccessKey = sixtyFours[0]!.toLowerCase();
  }
  if (!out.accessKeyId) {
    // Exclude anything already claimed, so the account id is not mistaken for
    // the access key — both are 32 hex characters.
    const claimed = new Set([out.accountId, out.secretAccessKey].filter(Boolean));
    const thirtyTwos = (text.match(HEX32) ?? [])
      .map((h) => h.toLowerCase())
      .filter((h) => !claimed.has(h) && !out.secretAccessKey?.includes(h));
    const unique = [...new Set(thirtyTwos)];
    if (unique.length === 1) out.accessKeyId = unique[0];
  }
  if (!out.accountId) {
    const claimed = new Set([out.accessKeyId, out.secretAccessKey].filter(Boolean));
    const thirtyTwos = (text.match(HEX32) ?? [])
      .map((h) => h.toLowerCase())
      .filter((h) => !claimed.has(h) && !out.secretAccessKey?.includes(h));
    const unique = [...new Set(thirtyTwos)];
    if (unique.length === 1) out.accountId = unique[0];
  }

  for (const k of ['accountId', 'accessKeyId', 'secretAccessKey', 'bucket'] as const) {
    if (out[k]) out.found.push(k);
  }
  return out;
}

export const FIELD_LABELS: Record<keyof R2Config, string> = {
  accountId: 'Account ID',
  bucket: 'Bucket name',
  accessKeyId: 'Access Key ID',
  secretAccessKey: 'Secret Access Key',
};
